import { useCallback, useState } from 'react';

import type { Transaction } from '@shared/transactions-types';
import { logger } from '@luna/logger';
import { t } from '@luna/i18n';

import { getAttachmentUrl } from './getAttachmentUrl';
import { useDownloadMethod } from './useDownloadMethod';

interface Returned {
    download(): Promise<void>;

    isPending: boolean;
    error: Error | undefined;
}

export const useDownloadAttachment = (transaction: Transaction): Returned => {
    const [error, setError] = useState<Error>();
    const [isPending, setIsPending] = useState(false);

    const handleError = useCallback((cause: any): void => {
        const message = t('transaction.attachment.downloadError');
        const wrappingError = new Error(message, { cause });
        setError(wrappingError);
        logger.error(cause);
        throw wrappingError;
    }, []);

    const downloadAttachment = useDownloadMethod(handleError);

    const download = useCallback(() => {
        setIsPending(true);
        setError(undefined);

        const attachmentUrl = getAttachmentUrl(transaction.id);

        return downloadAttachment(attachmentUrl)
            .catch(handleError)
            .finally(() => setIsPending(false));
    }, [transaction.id, downloadAttachment, handleError]);

    return { download, isPending, error };
};
